import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import en from "./locales/en.json";
import pt from "./locales/pt.json";
import enCA from "./locales/en-CA.json";
import frCA from "./locales/fr-CA.json";
import esMX from "./locales/es-MX.json";
import hi from "./locales/hi.json";

/**
 * EPIC-030 supported locale codes.
 *
 * Source-of-truth list. Adding a locale = drop a JSON file in
 * `./locales/`, register it here, and the rest is config-driven
 * (parity test picks it up automatically; LocalePicker reads this
 * list).
 */
export type SupportedLocale =
  | "en"
  | "pt"
  | "en-CA"
  | "fr-CA"
  | "es-MX"
  | "hi";
export const SUPPORTED_LOCALES: readonly SupportedLocale[] = [
  "en",
  "pt",
  "en-CA",
  "fr-CA",
  "es-MX",
  "hi",
] as const;

/**
 * EPIC-030 STORY-1803 — react-i18next bootstrap.
 *
 * The chrome-strings i18n layer. Distinct from the *content*
 * translations covered by EPIC-030 STORY-1801 (which handles market
 * title/description JSONB on the backend) — this layer covers UI
 * labels, button text, error messages, and other strings the React
 * components render directly.
 *
 * Locale resolution order:
 *   1. `users.locale` if logged in (via the `/v1/me` payload).
 *   2. Browser navigator.language as a fallback.
 *   3. 'en' if neither resolves.
 *
 * The locale catalog is statically imported (not lazy-loaded) for
 * v1: the chrome-string set is small enough that bundle size hit is
 * negligible, and statically imported catalogs avoid the SSR/Suspense
 * dance lazy-loaded i18next plugins require under Next.js App Router.
 *
 * Adding a locale = drop a JSON file in `./locales/`, register it in
 * the `resources` block below, surface it in `LocalePicker`'s allowed
 * values, and the rest is config-driven.
 */

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    pt: { translation: pt },
    "en-CA": { translation: enCA },
    "fr-CA": { translation: frCA },
    "es-MX": { translation: esMX },
    hi: { translation: hi },
  },
  lng: undefined, // resolver picks via fallbackLng on init; updateLocale() flips it later
  fallbackLng: {
    // STORY-1806 promotion (2026-05-01) — Phase 1 launch-order locales.
    // English-flavoured Canadian falls back to en first; French-Canadian
    // to French sources don't exist yet, so en is the safety net.
    "en-CA": ["en"],
    "fr-CA": ["en"],
    "es-MX": ["en"],
    // Hindi (EPIC requirement — IN markets, 2026-05-13). Falls back to
    // English for any admin-only keys that don't have a Hindi
    // translation yet — admin surfaces are en fallback ok.
    hi: ["en"],
    default: ["en"],
  },
  interpolation: {
    // React already escapes; double-escaping breaks the rendered output.
    escapeValue: false,
  },
  // Prevents the "missing key" console flood in dev when a brand-new
  // string is first added to an English catalog before mirrors land.
  // The key itself surfaces as the rendered text in that case
  // (acceptable — Polymarket-parity behaviour for incremental locale
  // adds).
  returnEmptyString: false,
});

/**
 * Switch the active locale at runtime. Called from the LocalePicker
 * dropdown and on /v1/me hydration when the user has a stored
 * `locale` preference. Persists nothing here — persistence happens
 * server-side via PATCH /v1/me/locale (existing endpoint).
 */
export function changeLocale(locale: SupportedLocale): Promise<unknown> {
  return i18n.changeLanguage(locale);
}

export default i18n;
